/*
 * TELA DE LOGIN — como ela se comporta (explicação em linguagem simples)
 *
 * 1. Erro de e-mail ou senha
 *    Quando o e-mail ou a senha estão errados, a tela sempre mostra a MESMA
 *    frase: "E-mail ou senha inválidos." Ela não diz se o e-mail existe ou não,
 *    para dificultar que alguém fique testando e-mails para descobrir quais são
 *    válidos.
 *
 * 2. Aviso antes da conta bloquear
 *    O sistema bloqueia a conta depois de 3 tentativas erradas de senha, e o
 *    desbloqueio só é feito pelo suporte. Por isso, ao errar a senha pela
 *    2ª vez, a tela mostra um aviso destacado pedindo para conferir os dados
 *    e a senha, e trava o botão "Entrar" por alguns segundos para a pessoa
 *    respirar antes da última tentativa.
 *
 * 3. Conta já bloqueada
 *    Se a conta já estiver bloqueada, a tela mostra a mensagem de bloqueio e
 *    desabilita o botão "Entrar". Ele só volta a funcionar quando a pessoa troca
 *    o e-mail digitado (para outra pessoa poder usar o mesmo computador).
 *
 * 4. Muitas tentativas seguidas
 *    Se houver muitas tentativas erradas em sequência (na mesma conta ou
 *    trocando de e-mail), a tela impõe uma pausa que vai aumentando (20s, 1min,
 *    5min...), com uma contagem regressiva na tela. É uma proteção contra
 *    programas que tentam adivinhar senhas.
 *
 * 5. Proteção contra robôs
 *    Existe um campo invisível na tela que uma pessoa nunca vê nem preenche.
 *    Programas automáticos costumam preencher tudo — se esse campo vier
 *    preenchido, o login é recusado. A tela também ignora envios feitos rápido
 *    demais (menos de 1,5s) sem ninguém ter digitado nada.
 *
 * 6. Quem já está logado
 *    Se a pessoa já estiver logada e abrir /login, ela é levada direto para a
 *    tela inicial do perfil dela.
 *
 * 7. Aviso para visitantes
 *    Visitantes não têm acesso a este sistema. A tela mostra um aviso com o
 *    link para a página certa de cadastro de visitante.
 *
 * Observação: estas proteções ficam no navegador. Elas dificultam tentativas
 * feitas pela tela, mas não substituem as travas do servidor.
 */

import React, { useState, useEffect, useRef } from 'react';
import './login.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faClock } from '@fortawesome/free-solid-svg-icons';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUsers } from '../../context/usersContext';
import { consumeSessionEndReason } from '../../services/authStorage';
import ConfirmModal from '../../components/modal/confirmModal';
import useLoginThrottle from '../../utils/useLoginThrottle';
import Logo from '../../assets/brasao_sejus.png';
import BalaoFala from '../../assets/icons/balaofala.svg';
import { getProductionSystemUrl, getTrainingEnvironmentMessage } from '../../utils/runtimeEnvironment';

// Envio mais rápido que isso, sem ninguém ter digitado, é tratado como robô.
const MIN_SUBMIT_MS = 1500;

const EXTERNAL_PROFILE_GROUPS = ['TJPI', 'POLÍCIA MILITAR / POLÍCIA CIVIL', 'DEFENSORIA'];

const normalizeGroup = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toUpperCase();

// Aviso quando a sessão terminou sozinha. É um modal que fica até o usuário fechar: no logout por
// inatividade ele normalmente não está olhando, e um toast sumiria antes de ele voltar.
const SESSION_END_MESSAGES = {
  inactivity: 'Sua sessão foi encerrada por inatividade. Faça login novamente.',
  expired: 'Sua sessão expirou. Faça login novamente.',
  ended: 'Sua sessão foi encerrada. Faça login novamente.',
};

// Para onde mandar quem já está logado ao cair em /login (espelha o restrictedRoute).
const resolveHomePath = (userInfo) => {
  const groups = Array.isArray(userInfo?.groups) ? userInfo.groups.map(normalizeGroup) : [];
  if (groups.includes(normalizeGroup('ADVOGADO(A)'))) return '/advogado';
  if (EXTERNAL_PROFILE_GROUPS.some((group) => groups.includes(normalizeGroup(group)))) return '/internos';
  return '/';
};

function Login() {
  const { login, isTokenValid, userInfo } = useUsers();
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Campo isca (honeypot): humano não vê, robô costuma preencher.
  const [honeypot, setHoneypot] = useState('')
  // Conta bloqueada pelo backend (HTTP 401). Trava a tela até trocar o e-mail.
  const [accountLocked, setAccountLocked] = useState(false)
  // Motivo do fim automático da sessão (inatividade, tempo do token, recusada pelo servidor).
  const [sessionEndNotice, setSessionEndNotice] = useState(null)
  const trainingEnvironmentMessage = getTrainingEnvironmentMessage();
  const productionSystemUrl = getProductionSystemUrl();

  const throttle = useLoginThrottle(email);

  const mountTimeRef = useRef(null);
  const hasInteractedRef = useRef(false);

  useEffect(() => {
    mountTimeRef.current = Date.now();
  }, []);

  // Lê o motivo uma vez só (o StrictMode roda o efeito duas vezes; a segunda leitura volta vazia).
  useEffect(() => {
    const reason = consumeSessionEndReason();
    if (SESSION_END_MESSAGES[reason]) setSessionEndNotice(reason);
  }, []);

  const markInteracted = () => {
    hasInteractedRef.current = true;
  };

  const getWhatsappUrl = () => {
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite';
    return `https://wa.me/5511913386582?text=${encodeURIComponent(`${greeting}, suporte SIGEPEN`)}`;
  };

  // Texto do aviso abaixo do botão, conforme a situação.
  const getLockMessage = () => {
    if (accountLocked) {
      return 'Sua conta está bloqueada por excesso de tentativas. Entre em contato com o suporte para desbloquear.';
    }
    if (throttle.failureCount === 2) {
      const base = 'Atenção: mais uma tentativa incorreta e sua conta será bloqueada. Verifique os dados corretamente.';
      return throttle.locked ? `${base} Você poderá tentar de novo em ${throttle.remainingLabel}.` : base;
    }
    if (throttle.locked) {
      return `Muitas tentativas. Tente novamente em ${throttle.remainingLabel}.`;
    }
    return '';
  };

  // Função para logar
  const handleLogin = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (accountLocked) {
      toast.error('Sua conta está bloqueada por excesso de tentativas. Entre em contato com o suporte para desbloquear.');
      return;
    }
    if (throttle.locked) {
      toast.error(`Aguarde ${throttle.remainingLabel} e tente novamente.`);
      return;
    }
    // Campo isca preenchido: quase certamente um robô.
    if (honeypot.trim() !== '') {
      toast.error('E-mail ou senha inválidos.');
      return;
    }
    // Envio rápido demais sem ninguém ter digitado.
    const elapsed = Date.now() - (mountTimeRef.current ?? 0);
    if (!hasInteractedRef.current && mountTimeRef.current && elapsed < MIN_SUBMIT_MS) {
      toast.error('Aguarde um instante e tente novamente.');
      return;
    }

    setIsSubmitting(true);
    try {
      const { ok, reason, retryAfterMs } = (await login(email.trim(), senha)) || {};

      if (ok) {
        throttle.clearFailures();
        setAccountLocked(false);
        return;
      }

      if (reason === 'locked') {
        setAccountLocked(true);
        return;
      }

      if (reason === 'invalid' || reason === 'rate_limit') {
        const state = throttle.registerFailure({ serverRetryAfterMs: retryAfterMs });
        if (reason === 'invalid' && state.failureCount === 2) {
          toast.error('Atenção: mais uma tentativa incorreta e sua conta será bloqueada. Verifique o Caps Lock e a senha.');
        } else if (state.locked) {
          toast.error(`Muitas tentativas. Aguarde ${state.remainingLabel} e tente novamente.`);
        }
      }
      // 'network' / 'server' / 'unknown': o toast já saiu de dentro de login(), e não conta bloqueio.
    } finally {
      setIsSubmitting(false);
    }
  };

  // Já está logado e abriu /login: manda para a tela inicial do perfil.
  if (isTokenValid() && userInfo) {
    return <Navigate to={resolveHomePath(userInfo)} replace />;
  }

  const lockMessage = getLockMessage();
  const isButtonDisabled = isSubmitting || throttle.locked || accountLocked;

  return (
    <div className="login-container">
      <div className='content-form'>
        <div className="login-box">
          <form onSubmit={handleLogin}>
            <img src={Logo} alt='Logo' />
            <h2>Login</h2>
            {trainingEnvironmentMessage && (
              <p className="login-training-badge">{trainingEnvironmentMessage}</p>
            )}
            {productionSystemUrl && (
              <a
                className="login-production-link-badge"
                href={productionSystemUrl}
                title="Abrir sistema oficial"
              >
                ACESSAR SISTEMA OFICIAL
              </a>
            )}
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={email}
              required
              disabled={isSubmitting}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              maxLength={254}
              onChange={(e) => {
                setEmail(e.target.value);
                if (accountLocked) setAccountLocked(false);
              }}
              onKeyDown={markInteracted}
              onPointerDown={markInteracted}
            />
            {/* Campo isca invisível — não remover. Ver comentário no topo do arquivo. */}
            <input
              type="text"
              name="fax"
              className="login-hp-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
            <div className="password-input">
              <input
                type={showPassword ? "text" : "password"}
                name="senha"
                placeholder="Senha"
                value={senha}
                required
                disabled={isSubmitting}
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={128}
                onChange={(e) => setSenha(e.target.value)}
                onKeyDown={markInteracted}
                onPointerDown={markInteracted}
              />
              <FontAwesomeIcon
                icon={showPassword ? faEyeSlash : faEye}
                className="eye-icon"
                onClick={() => !isSubmitting && setShowPassword(!showPassword)}
              />
            </div>
            <button className='btn-login' type="submit" disabled={isButtonDisabled}>
              {isSubmitting ? (
                <span className="btn-login-loading">
                  <span className="btn-login-spinner" />
                  Entrando...
                </span>
              ) : (
                'ENTRAR'
              )}
            </button>
            {lockMessage && (
              <p className="login-lock-message">{lockMessage}</p>
            )}
            <a
              className="login-support-link"
              href={getWhatsappUrl()}
              target="_blank"
              rel="noreferrer"
              title="Suporte exclusivo para servidores do sistema (9h às 12h)"
            >
              <img src={BalaoFala} alt="Suporte" className="login-support-icon" />
              <span className="login-support-text">Suporte exclusivo servidor (9h às 12h)</span>
            </a>
            <p className="login-visitor-notice">
              Esta área é exclusiva para servidores com acesso ao sistema. Se você é visitante,
            acesse essa pagina: {' '}
              <Link to="/cadastro-visitante">ir para o cadastro de visitante</Link>.
            </p>
          </form>
        </div>
      </div>
      <ConfirmModal
        isOpen={Boolean(sessionEndNotice)}
        icon={faClock}
        mainMessage="Sessão encerrada"
        secondaryMessage={SESSION_END_MESSAGES[sessionEndNotice]}
        onConfirm={() => setSessionEndNotice(null)}
        closeModal={() => setSessionEndNotice(null)}
        confirmLabel="Entendi"
        showCancel={false}
      />
    </div>
  );
}

export default Login;
